import { APP_VERSION } from "@secret-cabinet/contracts";
import { CircleAlert, RotateCcw } from "lucide-react";
import { Component, type ErrorInfo, type ReactNode } from "react";
import { Brand } from "./Brand";

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  failed: boolean;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error("Secret Cabinet could not render the current chamber.", {
      errorName: error.name,
      componentStack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (!this.state.failed) {
      return this.props.children;
    }
    return <AppFailure />;
  }
}

export function AppFailure() {
  return (
    <div className="app-failure">
      <header className="app-failure__header">
        <Brand />
        <span className="app-version">v{APP_VERSION}</span>
      </header>
      <main>
        <CircleAlert aria-hidden="true" />
        <p className="eyebrow">The live chamber stopped</p>
        <h1>Reload this screen safely</h1>
        <p>
          Your room is kept on the server. Reloading reconnects this device to its existing
          seat instead of creating a new player.
        </p>
        <button
          className="button button--primary"
          type="button"
          onClick={() => window.location.reload()}
        >
          <RotateCcw aria-hidden="true" /> Reload Secret Cabinet
        </button>
        <a className="text-link" href="https://games.xile.se">
          Return to Xile Games
        </a>
      </main>
    </div>
  );
}
